import { useState } from "react";
import { toast } from "react-hot-toast";
import CreateProductPopup from "../CreateProductPopup/CreateProductPopup";
import { updateSpotlightProductDuringApproval } from "../../../Service/user";
import "./Products.scss";

// Product Requests Modal Component
const RequestsModal = ({ isOpen, onClose, requests, onUpdateStatus, isAdmin, loading }) => {
	const [remarkText, setRemarkText] = useState("");
	const [showRemarkInput, setShowRemarkInput] = useState(false);
	const [selectedRequestId, setSelectedRequestId] = useState(null);
	const [pendingAction, setPendingAction] = useState(null);
	const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(null);

	if (!isOpen) return null;

	const handleShowRemark = (requestId, action) => {
		setSelectedRequestId(requestId);
		setShowRemarkInput(true);
		setPendingAction(action);
	};

	const handleSubmitWithRemark = () => {
		onUpdateStatus(selectedRequestId, pendingAction, remarkText);
		setShowRemarkInput(false);
		setRemarkText("");
		setSelectedRequestId(null);
		setPendingAction(null);
	};

	const handleEditProduct = (product) => {
		setSelectedProduct(product);
		setIsEditPopupOpen(true);
	};

	const handleProductUpdate = async (updatedData) => {
		try {
			// Calculate changes between original and updated data
			const changesObj = {};
			Object.keys(updatedData).forEach(key => {
				if (JSON.stringify(selectedProduct[key]) !== JSON.stringify(updatedData[key])) {
					changesObj[key] = {
						old: selectedProduct[key],
						new: updatedData[key]
					};
				}
			});

			const response = await updateSpotlightProductDuringApproval(selectedProduct._id, {
				updatedData,
				changes: changesObj
			});

			if (response.status === 200) {
				// Set the remark text to include the changes
				setRemarkText(response.remarkText);
				setShowRemarkInput(true);
				setPendingAction('approved');
				setSelectedRequestId(selectedProduct.requestId);
				toast.success('Product updated successfully');
			} else {
				toast.error(response.message || 'Failed to update product');
			}
		} catch (error) {
			console.error('Error updating product:', error);
			toast.error('An error occurred while updating the product');
		} finally {
			setIsEditPopupOpen(false);
			setSelectedProduct(null);
		}
	};

	const statusColors = {
		pending: "#FFC107",
		approved: "#4CAF50",
		rejected: "#F44336"
	};

	const getMonthName = (monthNumber) => {
		const months = [
			"January", "February", "March", "April", "May", "June",
			"July", "August", "September", "October", "November", "December"
		];
		return months[monthNumber - 1];
	};

	return (
		<div className="requests-modal-overlay">
			<div className="requests-modal">
				<div className="requests-modal-header">
					<h2>Product Requests</h2>
					<button className="close-button" onClick={onClose}>×</button>
				</div>
				<div className="requests-modal-content">
					{loading ? (
						<div className="loading-requests">
							<div className="loading-spinner"></div>
							<p>Loading requests...</p>
						</div>
					) : requests.length === 0 ? (
						<p className="no-requests">No product requests found.</p>
					) : (
						<div className="requests-list">
							{requests.map((request) => (
								<div key={request._id} className="request-item">
									<div className="request-header">
										<div className="requester-info">
											<img 
												src={request.requester?.profilePicture || "/placeholder.svg"} 
												alt="Profile" 
												className="requester-avatar"
											/>
											<div className="requester-details">
												<h3 className="requester-name">{`${request.requester?.firstName || ''} ${request.requester?.lastName || ''}`}</h3>
												<div className="submission-info">
													<span className="submission-month">
														{getMonthName(request.month)} {request.year}
													</span>
													{request.isResubmission && (
														<span className="resubmission-badge">Resubmission</span>
													)}
												</div>
												<span className="request-date">
													Submitted on {new Date(request.createdAt).toLocaleDateString()}
												</span>
											</div>
										</div>
										<div 
											className="request-status" 
											style={{ backgroundColor: statusColors[request.requestStatus] }}
										>
											{request.requestStatus.charAt(0).toUpperCase() + request.requestStatus.slice(1)}
										</div>
									</div>
									
									<div className="product-info">
										<div className="product-logo">
											<img src={request.productId?.logo || "/placeholder.svg"} alt="Logo" />
										</div>
										<div className="product-details">
											<h3>{request.productId?.title}</h3>
											<p>{request.productId?.tagline}</p>
											<div className="product-tags">
												<span className="tag"><b>{request.productId?.stage}</b></span>
												{request.productId?.tags?.map((tag, idx) => (
													<span key={idx} className="tag">{tag}</span>
												))}
												{request.productId?.industry && (
													<span className="tag industry">{request.productId.industry}</span>
												)}
											</div>
										</div>
										{isAdmin && request.requestStatus === "pending" && (
											<div className="admin-actions">
												<button 
													className="view-button"
													onClick={() => window.location.href = `/startup-spotlight/product/${request.productId?._id}`}
												>
													<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
														<circle cx="12" cy="12" r="3"></circle>
													</svg>
													View
												</button>
												<button 
													className="edit-button"
													onClick={() => handleEditProduct({...request.productId, requestId: request._id})}
												>
													<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
														<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
													</svg>
													Edit
												</button>
											</div>
										)}
									</div>

									{/* Display remarks if they exist */}
									{request.remarkText && (
										<div className="request-remarks">
											<h4>Remarks from Admin:</h4>
											<div className="remarks-content">
												{request.remarkText.split('\n').map((line, index) => (
													<p key={index}>{line}</p>
												))}
											</div>
										</div>
									)}

									{/* Monthly Submissions Section */}
									{request.productId?.monthlySubmissions && request.productId.monthlySubmissions.length > 0 && (
										<div className="monthly-submissions">
											<h4>Submission History</h4>
											<div className="submissions-timeline">
												{request.productId.monthlySubmissions.map((submission, index) => (
													<div 
														key={index} 
														className={`submission-entry ${submission.status}`}
													>
														<div className="submission-dot" style={{ 
															backgroundColor: statusColors[submission.status] || '#ccc'
														}}></div>
														<div className="submission-content">
															<span className="submission-date">
																{getMonthName(submission.month)} {submission.year}
															</span>
															<span className="submission-status" style={{
																color: statusColors[submission.status]
															}}>
																{submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
															</span>
														</div>
													</div>
												))}
											</div>
										</div>
									)}
									
									{request.productId?.stage != "Idea" && (
										<div className="pitch-deck-info">
											<h4>Pitch Deck Information</h4>
											<div className="pitch-deck-details">
												{request.productId.pitchDeck?.document && (
													<div className="pitch-deck-document">
														<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
															<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
															<polyline points="14 2 14 8 20 8"></polyline>
															<line x1="12" y1="18" x2="12" y2="12"></line>
															<line x1="9" y1="15" x2="15" y2="15"></line>
														</svg>
														<a 
															href={request.productId.pitchDeck?.document} 
															target="_blank" 
															rel="noopener noreferrer"
														>
															View Pitch Deck
														</a>
													</div>
												)}
												{request.productId.pitchDeck?.requestedFromAnalyst && (
													<div className="analyst-request">
														<p>
															<span className="request-badge">Requested from Analyst</span>
														</p>
														<div className="requester-contact">
															<p><strong>Contact Details:</strong></p>
															<p>Name: {`${request.requester?.firstName || ''} ${request.requester?.lastName || ''}`}</p>
															<p>Email: {request.requester?.email || 'N/A'}</p>
															<p>Phone: {request.requester?.phoneNumber || 'N/A'}</p>
														</div>
													</div>
												)}
											</div>
										</div>
									)}

									{/* KYC Documents Section */}
									{request.productId?.kycDocumentsIds && request.productId.kycDocumentsIds.length > 0 && (
										<div className="kyc-documents-info">
											<h4>KYC Documents</h4>
											<div className="kyc-documents-list">
												{request.productId.kycDocumentsIds.map((doc, index) => (
													<div key={index} className="kyc-document-item">
														<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
															<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
															<polyline points="14 2 14 8 20 8"></polyline>
															<line x1="12" y1="18" x2="12" y2="12"></line>
															<line x1="9" y1="15" x2="15" y2="15"></line>
														</svg>
														<a 
															href={doc.fileUrl} 
															target="_blank" 
															rel="noopener noreferrer"
														>
															{doc.fileName}
														</a>
													</div>
												))}
											</div>
										</div>
									)}

									{isAdmin && request.requestStatus === "pending" && (
										<>
											<div className="request-actions">
												<button 
													className="approve-button"
													onClick={() => handleShowRemark(request._id, "approved")}
												>
													<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<polyline points="20 6 9 17 4 12"></polyline>
													</svg>
													Approve
												</button>
												<button 
													className="reject-button"
													onClick={() => handleShowRemark(request._id, "rejected")}
												>
													<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<line x1="18" y1="6" x2="6" y2="18"></line>
														<line x1="6" y1="6" x2="18" y2="18"></line>
													</svg>
													Reject
												</button>
												<button 
													className="remark-button"
													onClick={() => {
														setSelectedRequestId(request._id);
														setShowRemarkInput(true);
													}}
												>
													<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
													</svg>
													Add Remark
												</button>
											</div>
											{showRemarkInput && selectedRequestId === request._id && (
												<div className="remark-input-container">
													<textarea
														value={remarkText}
														onChange={(e) => {
															setRemarkText(e.target.value);
														}}
														placeholder="Add your remark here (optional)"
														rows="3"
													/>
													<div className="remark-actions">
														<button 
															className="cancel-button"
															onClick={() => {
																setShowRemarkInput(false);
																setRemarkText("");
																setSelectedRequestId(null);
																setPendingAction(null);
															}}
														>
															Cancel
														</button>
														{pendingAction && (
															<button 
																className={`submit-button ${pendingAction}`}
																onClick={handleSubmitWithRemark}
															>
																{pendingAction === "approved" ? "Approve" : "Reject"} with Remark
															</button>
														)}
													</div>
												</div>
											)}
										</>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{isEditPopupOpen && selectedProduct && (
				<CreateProductPopup
					isOpen={isEditPopupOpen}
					onClose={() => {
						setIsEditPopupOpen(false);
						setSelectedProduct(null);
					}}
					onSubmit={handleProductUpdate}
					initialData={selectedProduct}
					isEditing={true}
				/>
			)}
		</div>
	);
};

export default RequestsModal; 